import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TOO_MANY_REQUESTS } from "../../helpers/requestErrors";

// Error codes the backend returns for Google/Apple/Microsoft sign-ins
export type SocialLoginErrorCode =
  | "SOCIAL_LINK_REQUIRED"
  | "SOCIAL_LINK_EXPIRED"
  | "SOCIAL_EMAIL_NOT_VERIFIED"
  | "SOCIAL_LOGIN_FAILED"
  | "INVALID_PASSWORD";

export type AuthErrorPayload = {
  message: string;
  status?: number;
  errorCode?: SocialLoginErrorCode | string;
  // Only with SOCIAL_LINK_REQUIRED
  linkToken?: string;
  email?: string;
  provider?: string;
};

// State the link page expects, passed via router state or URL fragment
export type SocialLinkState = {
  linkToken: string;
  email: string;
  provider: string;
};

export function toAuthError(error: any): AuthErrorPayload {
  const data = error?.response?.data;
  return {
    message: data?.message || error?.message || String(error),
    status: error?.response?.status,
    errorCode:
      data?.errorCode ??
      (error?.response?.status === 429 ? TOO_MANY_REQUESTS : undefined),
    linkToken: data?.linkToken,
    email: data?.email,
    provider: data?.provider,
  };
}

/**
 * Sends the user on after a failed Google/Apple sign-in: to the link page if
 * an unverified account with the email exists, otherwise back to the login
 * page, which shows the error.
 */
export function useSocialLoginErrorHandler() {
  const navigate = useNavigate();

  return useCallback(
    (error?: AuthErrorPayload) => {
      if (error?.errorCode === "SOCIAL_LINK_REQUIRED" && error.linkToken) {
        const state: SocialLinkState = {
          linkToken: error.linkToken,
          email: error.email ?? "",
          provider: error.provider ?? "",
        };
        navigate("/link-account", { state, replace: true });
        return;
      }
      const code = error?.errorCode ?? "SOCIAL_LOGIN_FAILED";
      navigate(`/login?oauthError=${encodeURIComponent(code)}`, {
        replace: true,
      });
    },
    [navigate],
  );
}
